import { create } from 'zustand';
import { Node, Edge, addEdge, Connection, applyNodeChanges, applyEdgeChanges, NodeChange, EdgeChange } from 'reactflow';
import {
  AgeMode,
  ExperimentType,
  ExperimentNodeData,
  SimulationResult,
  SimulationStatus,
  PresetExperiment,
} from '../types';
import { runExperiment, ExperimentConfig } from '../engine/quantumEngine';
import { PRESET_EXPERIMENTS } from '../experiments/presets';

interface SimulatorState {
  // ─── Age / Level ──────────────────────────────────────────────────────────
  ageMode: AgeMode;
  setAgeMode: (mode: AgeMode) => void;

  // ─── ReactFlow Graph ──────────────────────────────────────────────────────
  nodes: Node<ExperimentNodeData>[];
  edges: Edge[];
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;
  addNode: (node: Node<ExperimentNodeData>) => void;
  removeNode: (id: string) => void;

  // ─── Simulation ───────────────────────────────────────────────────────────
  status: SimulationStatus;
  result: SimulationResult | null;
  numParticles: number;
  setNumParticles: (n: number) => void;
  runSimulation: () => void;
  resetSimulation: () => void;
  stepSimulation: () => void;
  stepCount: number;

  // ─── Presets ──────────────────────────────────────────────────────────────
  activePreset: PresetExperiment | null;
  loadPreset: (id: string) => void;

  // ─── UI State ─────────────────────────────────────────────────────────────
  selectedNodeId: string | null;
  setSelectedNodeId: (id: string | null) => void;
  showHint: boolean;
  toggleHint: () => void;
}

/** Infer experiment type from nodes present on the canvas */
function inferExperimentType(nodes: Node<ExperimentNodeData>[]): ExperimentType {
  const types = nodes.map((n) => n.data?.componentType);
  if (types.includes('double-slit')) {
    if (types.includes('measurement-detector')) return 'measurement-collapse';
    return 'double-slit';
  }
  if (types.includes('beam-splitter')) return 'beam-splitter';
  // Entanglement requires two qubit sources – check this BEFORE hadamard-gate
  // so the preset isn't mis-classified as a plain qubit circuit.
  if (types.filter((t) => t === 'qubit-source').length >= 2) return 'entanglement';
  if (types.includes('hadamard-gate')) return 'qubit-circuit';
  if (types.some((t) => t === 'qubit-source')) return 'qubit-circuit';
  return 'custom';
}

/** Build ExperimentConfig from nodes */
function buildConfig(
  nodes: Node<ExperimentNodeData>[],
  numParticles: number
): ExperimentConfig {
  const type = inferExperimentType(nodes);
  const slitNode = nodes.find((n) => n.data?.componentType === 'double-slit');
  const phaseNode = nodes.find((n) => n.data?.componentType === 'phase-shifter');
  const measureNode = nodes.find(
    (n) => n.data?.componentType === 'measurement-detector'
  );

  return {
    type,
    numParticles,
    withMeasurement: !!measureNode && type !== 'qubit-circuit',
    slitSeparation: Number(slitNode?.data?.properties?.slitSeparation ?? 60),
    phaseShiftDeg: Number(phaseNode?.data?.properties?.phaseDeg ?? 0),
    wavelength: 20,
    screenDistance: 200,
    screenWidth: 400,
    screenHeight: 300,
  };
}

export const useSimulatorStore = create<SimulatorState>((set, get) => ({
  ageMode: 'beginner',
  setAgeMode: (mode) => set({ ageMode: mode }),

  nodes: [],
  edges: [],

  onNodesChange: (changes) =>
    set((s) => ({ nodes: applyNodeChanges(changes, s.nodes) as Node<ExperimentNodeData>[] })),
  onEdgesChange: (changes) =>
    set((s) => ({ edges: applyEdgeChanges(changes, s.edges) })),
  onConnect: (connection) =>
    set((s) => ({
      edges: addEdge({ ...connection, animated: true, style: { stroke: '#6366f1', strokeWidth: 2 } }, s.edges),
    })),
  addNode: (node) =>
    set((s) => ({ nodes: [...s.nodes, node] })),
  removeNode: (id) =>
    set((s) => ({
      nodes: s.nodes.filter((n) => n.id !== id),
      edges: s.edges.filter((e) => e.source !== id && e.target !== id),
    })),

  status: 'idle',
  result: null,
  numParticles: 200,
  stepCount: 0,
  setNumParticles: (n) => set({ numParticles: n }),

  runSimulation: () => {
    const { nodes, numParticles } = get();
    if (nodes.length === 0) return;
    set({ status: 'running', result: null });

    // Short async delay for visual feedback
    setTimeout(() => {
      try {
        const config = buildConfig(nodes, numParticles);
        const result = runExperiment(config);
        set({ status: 'complete', result, stepCount: 0 });
      } catch (e) {
        set({ status: 'error' });
      }
    }, 600);
  },

  resetSimulation: () =>
    set({ status: 'idle', result: null, stepCount: 0 }),

  stepSimulation: () => {
    const { nodes, stepCount, numParticles } = get();
    if (nodes.length === 0) return;
    set({ status: 'running' });

    setTimeout(() => {
      try {
        const step = Math.min(stepCount + 50, numParticles);
        const config = buildConfig(nodes, step);
        const result = runExperiment(config);
        set({
          status: step >= numParticles ? 'complete' : 'running',
          result,
          stepCount: step,
        });
      } catch (e) {
        set({ status: 'error' });
      }
    }, 100);
  },

  activePreset: null,
  loadPreset: (id) => {
    const preset = PRESET_EXPERIMENTS.find((p) => p.id === id);
    if (!preset) return;
    set({
      activePreset: preset,
      nodes: preset.nodes.map((pn) => ({
        id: pn.id,
        type: 'experimentNode',
        position: pn.position,
        data: pn.data as ExperimentNodeData,
      })),
      edges: preset.edges.map((pe) => ({
        id: pe.id,
        source: pe.source,
        target: pe.target,
        sourceHandle: pe.sourceHandle,
        targetHandle: pe.targetHandle,
        animated: true,
        style: { stroke: '#6366f1', strokeWidth: 2 },
      })),
      status: 'idle',
      result: null,
    });
  },

  selectedNodeId: null,
  setSelectedNodeId: (id) => set({ selectedNodeId: id }),
  showHint: false,
  toggleHint: () => set((s) => ({ showHint: !s.showHint })),
}));
